import { setLoadingScreenStatus, formatLoadingScreenError } from '../appStatus';
import { appStatusState } from '../react/AppStatusProvider';
import { hideNotification, showNotification } from '../react/NotificationProvider';
import { pixelartIcons } from '../react/PixelartIcon';
const NO_STAGES_ACTION_END = false;
const createProgressReporter = (implementation) => {
    const stages = new Map();
    let currentMessage;
    let ended = false;
    const end = () => {
        if (ended)
            return;
        ended = true;
        stages.clear();
        implementation.end();
    };
    const updateStatus = () => {
        if (ended)
            return;
        const activeStages = [...stages.entries()];
        if (activeStages.length === 0) {
            if (NO_STAGES_ACTION_END) {
                end();
            }
            else {
                implementation.setMessage('Waiting for tasks');
            }
            return;
        }
        const [currentStage, info] = activeStages.at(-1);
        let message = info.title;
        if (info.subStage) {
            message += ` - ${info.subStage}`;
        }
        if (info.progress !== undefined) {
            const num = Math.round(info.progress * 100);
            if (isFinite(num)) {
                message += `: ${num}%`;
            }
        }
        currentMessage = message;
        implementation.setMessage(message);
    };
    const reporter = {
        beginStage(stage, title) {
            if (stages.has(stage)) {
                throw new Error(`Stage ${stage} already is running`);
            }
            stages.set(stage, { title });
            updateStatus();
        },
        endStage(stage) {
            stages.delete(stage);
            updateStatus();
        },
        setSubStage(stage, subStageTitle) {
            const info = stages.get(stage);
            if (info) {
                info.subStage = subStageTitle;
                updateStatus();
            }
        },
        reportProgress(stage, progress) {
            const info = stages.get(stage);
            if (info) {
                info.progress = progress;
                updateStatus();
            }
        },
        async executeWithMessage(...args) {
            const message = args[0];
            const stage = typeof args[1] === 'string' ? args[1] : undefined;
            const fn = typeof args[1] === 'string' ? args[2] : args[1];
            const tempStage = stage ?? 'temp-' + Math.random().toString(36).slice(2);
            reporter.beginStage(tempStage, message);
            try {
                const result = await fn();
                return result;
            }
            finally {
                reporter.endStage(tempStage);
            }
        },
        end() {
            end();
        },
        setMessage(message) {
            if (ended)
                return;
            implementation.setMessage(message);
        },
        get currentMessage() {
            return currentMessage;
        },
        error(message) {
            if (ended)
                return;
            implementation.error(message);
        }
    };
    return reporter;
};
const fullScreenReporters = [];
export const createFullScreenProgressReporter = () => {
    const reporter = createProgressReporter({
        setMessage(message) {
            if (appStatusState.isError)
                return;
            setLoadingScreenStatus(message);
        },
        end() {
            if (appStatusState.isError)
                return;
            fullScreenReporters.splice(fullScreenReporters.indexOf(reporter), 1);
            if (fullScreenReporters.length === 0) {
                setLoadingScreenStatus(undefined);
            }
            else {
                setLoadingScreenStatus(fullScreenReporters.at(-1).currentMessage);
            }
        },
        error(message) {
            if (appStatusState.isError)
                return;
            setLoadingScreenStatus(formatLoadingScreenError('Progress reporter', message), true);
        }
    });
    fullScreenReporters.push(reporter);
    return reporter;
};
export const createNotificationProgressReporter = (endMessage) => {
    const id = `progress-reporter-${Math.random().toString(36).slice(2)}`;
    return createProgressReporter({
        setMessage(message) {
            showNotification(`${message}...`, '', false, '', undefined, true, id);
        },
        end() {
            if (endMessage) {
                showNotification(endMessage, '', false, pixelartIcons.check, undefined, true);
            }
            else {
                hideNotification(id);
            }
        },
        error(message) {
            showNotification(message, '', true, '', undefined, true);
        }
    });
};
export const createConsoleLogProgressReporter = (group) => {
    return createProgressReporter({
        setMessage(message) {
            console.log(group ? `[${group}] ${message}` : message);
        },
        end() {
            console.log(group ? `[${group}] done` : 'done');
        },
        error(message) {
            console.error(message);
        }
    });
};
export const createWrappedProgressReporter = (reporter, message) => {
    const stage = `wrapped-${message}`;
    if (message) {
        reporter.beginStage(stage, message);
    }
    return createProgressReporter({
        setMessage(message) {
            reporter.setMessage(message);
        },
        end() {
            if (message) {
                reporter.endStage(stage);
            }
        },
        error(message) {
            reporter.error(message);
        }
    });
};
export const createNullProgressReporter = () => {
    return createProgressReporter({
        setMessage(message) {
        },
        end() {
        },
        error(message) {
        }
    });
};
